import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import dayjs from 'dayjs';
// en is included by default
import 'dayjs/locale/es';
import Heading from '@athena/spark/Heading';
import Input from '@athena/spark/Input';
import Checkbox from '@athena/spark/Checkbox';
import Signpost from '@athena/spark/Signpost';
import Loader from '@athena/spark/Loader';
import Banner from '@athena/spark/Banner';
import BannerItem from '@athena/spark/BannerItem';
import cx from 'classnames';
import Select from '@athena/spark/Select';
import { appInsights } from 'src/shared/lib/appInsights';
import { getAthenaContextId } from 'src/components/utils/Helper';
import { PatientRelationship } from 'src/typings/globals';
import { getJoinToken } from 'src/components/utils/Helper';
import { useAppState } from 'src/components/provider/AppStateProvider';
import { useAppointmentState } from 'src/components/provider/AppointmentProvider';
import { RoutePaths } from 'src/workflow/routeMap';
import { AmplitudeButton } from 'src/components/container/AmplitudeButton/AmplitudeButton';
import { useAmplitude } from 'src/lib/amplitude';
import { useFlags } from 'src/shared/components/LaunchDarkly';
import ReminderIcon from 'src/assets/BlueReminderPointer.svg';
import welcomePageIcon from 'src/assets/welcomePageIcon.svg';
import ConsentModal from 'src/components/ui/ConsentModal';
import { contextConsentMap } from 'src/components/ui/TermsOfUse';
import styles from './WelcomePage.module.scss';

export default function WelcomePage() {
  const [inputName, setInputName] = useState('');
  const [inputNameError, setInputNameError] = useState<string>('');
  const [inputTouched, setInputTouched] = useState(false);
  const navigate = useNavigate();
  const { setAppState } = useAppState();
  const { t } = useTranslation();
  const { apptLoading, providerName, start: apptTime, brandName } = useAppointmentState();
  const { ampTrackEvent, ampTrackPageView } = useAmplitude();
  const {
    flags: { optumTermsAndConditions },
  } = useFlags();
  const contextId = getAthenaContextId() || '';
  const title = t('TITLE_VISIT_SIGN_IN');
  document.title = title;

  const relationshipOptions = [
    {
      text: t('LANDING_PAGE_PATIENT_DROPDOWN_OPTION_1'),
      value: PatientRelationship.PATIENT,
    },
    {
      text: t('LANDING_PAGE_PATIENT_DROPDOWN_OPTION_2'),
      value: PatientRelationship.CAREGIVER,
    },
    {
      text: t('LANDING_PAGE_PATIENT_DROPDOWN_OPTION_3'),
      value: PatientRelationship.FAMILY,
    },
    {
      text: t('LANDING_PAGE_PATIENT_DROPDOWN_OPTION_4'),
      value: PatientRelationship.FRIEND,
    },
    {
      text: t('LANDING_PAGE_PATIENT_DROPDOWN_OPTION_5'),
      value: PatientRelationship.PROVIDER,
    },
    {
      text: t('LANDING_PAGE_PATIENT_DROPDOWN_OPTION_6'),
      value: PatientRelationship.INTERPRETER,
    },
    {
      text: t('LANDING_PAGE_PATIENT_DROPDOWN_OPTION_7'),
      value: PatientRelationship.OTHER,
    },
  ];

  const [submitListener, setSubmitListener] = useState<boolean>(false);
  const [patientRelationship, setPatientRelationship] = useState<string>(relationshipOptions[0].value);
  const [patientConsented, setPatientConsented] = useState<boolean>(false);
  const [supplementalConsented, setSupplementalConsented] = useState<boolean>(false);
  const [showAthenaConsentModal, setShowAthenaConsentModal] = useState<boolean>(false);
  const [showSupplementalConsentModal, setShowSupplementalConsentModal] = useState<boolean>(false);
  const [showPatientMustConsentBanner, setShowPatientMustConsentBanner] = useState<string>('');
  const [showSupplementalConsentBanner, setShowSupplementalConsentBanner] = useState<string>('');
  const showCustomTermsOfUse = optumTermsAndConditions && contextId && contextConsentMap[contextId];

  const formIsValid =
    inputName.length > 0 &&
    patientConsented &&
    patientRelationship.length > 0 &&
    (showCustomTermsOfUse ? supplementalConsented : true);
  const linkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (!apptLoading && apptTime) {
      ampTrackPageView();

      if (optumTermsAndConditions && contextId && !contextConsentMap[contextId]) {
        appInsights.trackException({
          exception: new Error(`Custom terms and conditions enabled for context but content doesn't exist`),
          severityLevel: SeverityLevel.Warning,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apptLoading, apptTime]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (!inputTouched && inputName) {
      setInputTouched(true);
      ampTrackEvent({ action: 'EnterName' });
      timeout = setTimeout(() => setInputTouched(false), 5000);
    }
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputName]);

  const onInputChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputName(event.target.value);
    setInputNameError('');
  };

  const onConsentChanged = () => {
    setShowPatientMustConsentBanner('');
    ampTrackEvent({ action: 'T&C_Checkbox', data: { 'Common::Checked': !patientConsented } });
    setPatientConsented((consent) => !consent);
  };

  const onSupplementalConsentChanged = () => {
    setShowSupplementalConsentBanner('');
    ampTrackEvent({ action: 'SupplementalT&C_Checkbox', data: { 'Common::Checked': !supplementalConsented } });
    setSupplementalConsented((consent) => !consent);
  };

  const onSelectChanged = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setPatientRelationship(
      relationshipOptions.find((r) => r.value === event.target.value)?.value || PatientRelationship.OTHER
    );
  };

  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    setSubmitListener(!submitListener);
    event.preventDefault();
    const joinToken = getJoinToken();

    if (!formIsValid) {
      setInputNameError(inputName.length === 0 ? 'Name Required' : '');
      setShowPatientMustConsentBanner(patientConsented ? '' : 'You need to consent');
      setShowSupplementalConsentBanner(
        (optumTermsAndConditions ? supplementalConsented : true) ? '' : 'You need to consent'
      );
      return;
    }

    setAppState({ attendeeName: `${inputName.trim()}, ${patientRelationship}` });

    // saves joinToken from url into sessionStorage.  Enables redirecting back to
    // welcome page when in-call routes are refreshed
    sessionStorage.setItem('joinToken', joinToken);
    navigate(RoutePaths.MeetingInit);
  };

  const translatedApptTime = t('LANDING_PAGE_APPT_START_DATE', {
    date: dayjs(apptTime).format('dddd'),
    time: dayjs(apptTime).format('h:mm a'),
  });

  // Voice Over alert modifications https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/alert_role
  useEffect(() => {
    const hiddenInputAlertContainer = document.getElementById('hiddenInputAlertContainer');
    const hiddenConsentAlertContainer = document.getElementById('hiddenConsentAlertContainer');
    const hiddenSecondaryConsentAlertContainer = document.getElementById('hiddenSecondaryConsentAlertContainer');

    // ensures only one error is read aloud at a time
    if (hiddenSecondaryConsentAlertContainer && !supplementalConsented) {
      hiddenSecondaryConsentAlertContainer.innerHTML = '';
      // timeouts are necessary here to throw both actions on the event loop and ensure we get an alert every time a user hits 'next'
      setTimeout(() => {
        setTimeout(() => {
          document.getElementById('supplemental-consent-check')?.focus(); // we want control over exactly when this happens so the screenreader announces in order
        }, 2000);
        hiddenSecondaryConsentAlertContainer.innerHTML = t('LANDING_PAGE_CONSENT_ERROR');
      }, 100);
    }
    if (hiddenConsentAlertContainer && !patientConsented) {
      hiddenConsentAlertContainer.innerHTML = '';
      // timeouts are necessary here to throw both actions on the event loop and ensure we get an alert every time a user hits 'next'
      setTimeout(() => {
        setTimeout(() => {
          document.getElementById('consent-check')?.focus(); // we want control over exactly when this happens so the screenreader announces in order
        }, 2000);
        hiddenConsentAlertContainer.innerHTML = t('LANDING_PAGE_CONSENT_ERROR');
      }, 100);
    }
    if (hiddenInputAlertContainer && inputName.length === 0) {
      hiddenInputAlertContainer.innerHTML = '';
      setTimeout(() => {
        setTimeout(() => {
          document.getElementById('full-name')?.focus();
        }, 2000);
        hiddenInputAlertContainer.innerHTML = t('LANDING_PAGE_NAME_ERROR');
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitListener]);

  return (
    // TODO put the fixedCenter prop back on the loader component & update spark version when the forge bug is fixed here: https://athenajira.athenahealth.com/browse/SPARK-453
    <Loader className={styles.apptLoader} text={t('LOADING_APPOINTMENT_2')} loading={apptLoading}>
      {
        // The <Loader> component above does /not/ remove its children while loading and instead
        // overlays a semi-transparent layer along with the loading spinner. The boolean expression
        // below is used to prevent the appointment UI from rendering while the appointment is loading.
        !apptLoading && (
          <div className={styles.pageWrapper}>
            <ConsentModal
              show={showAthenaConsentModal}
              brandName={'athenaTelehealth'}
              contextId={'athenaTelehealth'} // default value for athenaT&C
              onClose={() => setShowAthenaConsentModal(false)}
            />

            <div className={styles.background}>
              <div className={styles.imgWrapperMobile}>
                <img alt={t('IMAGE_ALT_CATWOMAN')} className={styles.welcomPageIconMobile} src={welcomePageIcon} />
              </div>
              <div className={styles.contentWrapper}>
                <div tabIndex={-1} aria-live="polite" className={`${styles.headingWrapper} fe_u_margin--bottom-medium`}>
                  <Heading variant="subsection">{t('LANDING_PAGE_APPT_DETAILS_INTRO')}</Heading>
                  <Heading className={`${styles.welcomeHeading}`}>
                    {t('LANDING_PAGE_APPT_DETAILS_PROVIDER', { providerName })} <br role="presentation" />{' '}
                    {translatedApptTime}
                  </Heading>
                </div>
                <form data-testid="info-form" onSubmit={onFormSubmit} className={styles.patientInfoForm}>
                  <div className={`fe_u_margin--bottom-medium`}>
                    <label className={styles.label} htmlFor="full-name" data-testid="name-input">
                      {t('LANDING_PAGE_NAME_LABEL_2')} *
                    </label>
                    <Input
                      tabIndex={1}
                      error={inputNameError}
                      onChange={onInputChanged}
                      layout="large"
                      id="full-name"
                      placeholder={t('LANDING_PAGE_NAME_PLACEHOLDER_2')}
                      aria-required="true"
                      maxLength={50}
                    />
                    {inputNameError && (
                      <Banner className={`${styles.banner}`} alertType="attention">
                        <BannerItem className={styles.bannerItem}>
                          <p data-testid="alert-banner-name">{t('LANDING_PAGE_NAME_ERROR')}</p>
                          <div id="hiddenInputAlertContainer" role="alert" className={styles.visuallyHidden} />
                        </BannerItem>
                      </Banner>
                    )}
                  </div>
                  <div className={`fe_u_margin--bottom-medium`}>
                    <label className={styles.label} htmlFor="patient-relationship">
                      {t('LANDING_PAGE_PATIENT_LABEL')}
                    </label>
                    <Select
                      tabIndex={2}
                      layout="large"
                      hidePlaceholder
                      onChange={onSelectChanged}
                      className={`fe_u_fill--width`}
                      defaultValue={patientRelationship}
                      options={relationshipOptions}
                      id="patient-relationship"
                    />
                  </div>
                  <div className={`fe_u_flex-container`}>
                    <Checkbox
                      tabIndex={3}
                      className={styles.consentCheck}
                      onChange={onConsentChanged}
                      id="consent-check"
                      error={showPatientMustConsentBanner}
                      description={t('LANDING_PAGE_TERMS_NO_MARKUP')}
                      layout="large"
                      aria-required="true"
                    />
                    <p className={cx(`fe_u_font-size--small`, styles.checkboxLabel)}>
                      {t('LANDING_PAGE_TERMS')}
                      <a
                        data-testid="terms-link"
                        tabIndex={4}
                        ref={linkRef}
                        onClick={() => {
                          setShowAthenaConsentModal(true);
                          ampTrackEvent({ action: 'T&CLink' });
                          linkRef.current?.blur();
                        }}
                        href="#"
                        className={styles.checkboxLabelLink}
                      >
                        {t('LANDING_PAGE_TERMS_HEADING')}
                      </a>
                    </p>
                  </div>
                  {showPatientMustConsentBanner && (
                    <Banner className={`${styles.banner}`} alertType="attention">
                      <BannerItem className={styles.bannerItem}>
                        <p data-testid="alert-banner-consent">{t('LANDING_PAGE_CONSENT_ERROR')}</p>
                        <div id="hiddenConsentAlertContainer" role="alert" className={styles.visuallyHidden} />
                      </BannerItem>
                    </Banner>
                  )}
                  {showCustomTermsOfUse && (
                    <>
                      <div className={`fe_u_flex-container`}>
                        <Checkbox
                          tabIndex={5}
                          className={styles.consentCheck}
                          onChange={onSupplementalConsentChanged}
                          id="supplemental-consent-check"
                          error={showSupplementalConsentBanner}
                          description={t('LANDING_PAGE_TERMS_CONSENT') + t('LANDING_PAGE_CONSENT_HEADING')}
                          layout="large"
                          aria-required="true"
                        />
                        <p className={cx(`fe_u_font-size--small`, styles.checkboxLabel)}>
                          {t('LANDING_PAGE_TERMS_CONSENT')}
                          <a
                            data-testid="supplemental-terms-link"
                            tabIndex={6}
                            ref={linkRef}
                            onClick={() => {
                              setShowSupplementalConsentModal(true);
                              ampTrackEvent({ action: 'SupplementalT&CLink' });
                              linkRef.current?.blur();
                            }}
                            href="#"
                            className={styles.checkboxLabelLink}
                          >
                            {t('LANDING_PAGE_CONSENT_HEADING')}
                          </a>
                        </p>
                      </div>
                      <ConsentModal
                        show={showSupplementalConsentModal}
                        brandName={brandName}
                        contextId={contextId}
                        onClose={() => setShowSupplementalConsentModal(false)}
                      />
                    </>
                  )}
                  {showCustomTermsOfUse && showSupplementalConsentBanner && (
                    <Banner className={`${styles.banner}`} alertType="attention">
                      <BannerItem className={styles.bannerItem}>
                        <p data-testid="alert-banner-consent">{t('LANDING_PAGE_CONSENT_ERROR')}</p>
                        <div id="hiddenSecondaryConsentAlertContainer" role="alert" className={styles.visuallyHidden} />
                      </BannerItem>
                    </Banner>
                  )}
                  <Signpost media={<img alt="Reminder Icon" src={ReminderIcon} />} type="positive">
                    <p>
                      <Trans i18nKey="LANDING_PAGE_SIGNPOST">
                        Click <strong>Allow</strong> when your browser asks for access to your camera and microphone.
                      </Trans>
                    </p>
                  </Signpost>
                  <AmplitudeButton
                    action="Next"
                    eventProperties={{
                      'THWebApp_Welcome::Relationship': patientRelationship,
                      'Common::FormValid': formIsValid,
                    }}
                    pageName="Welcome"
                    tabIndex={5}
                    type="submit"
                    id="NextButton"
                    data-testid="next-btn"
                    size="large"
                    className={cx('fe_u_fill--width', 'fe_u_margin--top-medium')}
                  >
                    {t('LANDING_PAGE_NEXT_BUTTON')}
                  </AmplitudeButton>
                </form>
                <div className={styles.imgWrapper}>
                  <img alt={t('IMAGE_ALT_CATWOMAN')} className={styles.welcomePageIcon} src={welcomePageIcon} />
                </div>
              </div>
              <div className={styles.imgWrapperLarge}>
                <img alt={t('IMAGE_ALT_CATWOMAN')} className={styles.welcomePageIcon} src={welcomePageIcon} />
              </div>
            </div>
          </div>
        )
      }
    </Loader>
  );
}
